// src/pages/RoommateConnectPage.js
import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Import motion from framer-motion
import { motion } from 'framer-motion';

import './RoommateConnectPage.css';
// Removed '../App.css' import because we're replacing its animation with framer-motion
// If App.css contains other essential global styles, you might need to keep it.
// For animation purposes, it's no longer needed here.

const RoommateConnectPage = () => {
    // --- State for actual listings data ---
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Filter States ---
    const [selectedPropertyType, setSelectedPropertyType] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    // NEW FILTER STATE:
    const [selectedGenderPreference, setSelectedGenderPreference] = useState(''); // State for gender filter

    // --- New states for Autocomplete ---
    const [locationSuggestions, setLocationSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // --- Framer Motion Variants ---
    // These define how elements will animate

    const pageVariants = {
        initial: { opacity: 0, scale: 0.95 },
        animate: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.95, transition: { duration: 0.5, ease: "easeIn" } }
    };

    const sectionVariants = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0, transition: { delay: 0.4, duration: 0.7, ease: "easeOut" } }
    };

    const listingCardVariants = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0, transition: { delay: 1.2, duration: 0.5, ease: "easeOut" } }
    };

    const heartVariants = {
        initial: { opacity: 0, scale: 0.5 },
        animate: { opacity: 1, scale: 1, transition: { delay: 0.8, duration: 0.6, type: "ease", stiffness: 150, damping: 10 } }
    };

    // --- Fetch Listings from Firebase Firestore on component mount ---
    useEffect(() => {
        const fetchListings = async () => {
            setLoading(true);
            setError(null);
            try {
                const q = query(collection(db, 'website_listings'), orderBy('createdAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const fetchedListings = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setListings(fetchedListings);
            } catch (err) {
                console.error("Error fetching listings:", err);
                setError("Failed to load listings. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchListings();
    }, []);

    // Dynamically get filter options from fetched data
    const uniqueLocations = useMemo(() => {
        const locations = new Set();
        listings.forEach(listing => locations.add(listing.location));
        return Array.from(locations).sort();
    }, [listings]);

    const uniquePropertyTypes = useMemo(() => {
        const types = new Set();
        listings.forEach(listing => types.add(listing.propertyType));
        return ['All', ...Array.from(types).sort()];
    }, [listings]);

    // --- New function for Location Autocomplete Input Change ---
    const handleLocationInputChange = (e) => {
        const value = e.target.value;
        setSelectedLocation(value);

        if (value.length > 0) {
            const filtered = uniqueLocations.filter(loc =>
                loc.toLowerCase().includes(value.toLowerCase())
            ).sort();
            setLocationSuggestions(filtered);
            setShowSuggestions(true);
        } else {
            setLocationSuggestions([]);
            setShowSuggestions(false);
        }
    };

    // --- New function for Location Autocomplete Suggestion Click ---
    const handleSuggestionClick = (suggestion) => {
        setSelectedLocation(suggestion);
        setLocationSuggestions([]);
        setShowSuggestions(false);
    };

    // Function to apply filters - this useMemo re-calculates whenever any filter state changes
    const filteredListings = useMemo(() => {
        return listings.filter(listing => {
            const { location, rentPrice, propertyType, genderPreference } = listing; // Destructure genderPreference

            if (selectedPropertyType && selectedPropertyType !== 'All' && propertyType !== selectedPropertyType) {
                return false;
            }

            if (selectedLocation && !location.toLowerCase().includes(selectedLocation.toLowerCase())) {
                return false;
            }

            const price = parseFloat(rentPrice);
            const min = parseFloat(minPrice);
            const max = parseFloat(maxPrice);

            if (minPrice !== '' && !isNaN(min) && price < min) {
                return false;
            }
            if (maxPrice !== '' && !isNaN(max) && price > max) {
                return false;
            }

            // NEW FILTERING LOGIC FOR GENDER PREFERENCE
            if (selectedGenderPreference && genderPreference !== selectedGenderPreference) {
                return false;
            }
            
            return true;
        });
    }, [listings, selectedLocation, selectedPropertyType, minPrice, maxPrice, selectedGenderPreference]);

    // Function to clear all filters
    const clearFilters = () => {
        setSelectedPropertyType('');
        setSelectedLocation('');
        setMinPrice('');
        setMaxPrice('');
        setLocationSuggestions([]);
        setShowSuggestions(false);
        setSelectedGenderPreference(''); // Clear gender preference filter as well
    };

    // Replace plain divs with motion.div for loading/error/empty states
    // And remove the `page-fade-in` CSS class as framer-motion handles it
    if (loading) return (
        <motion.div
            className="listings-loading"
            initial="initial"
            animate="animate"
            variants={pageVariants}
        >
            Loading Roomease listings...
        </motion.div>
    );

    if (error) return (
        <motion.div
            className="listings-error"
            initial="initial"
            animate="animate"
            variants={pageVariants}
        >
            Error loading listings: {error}
        </motion.div>
    );

    const areFiltersActive = selectedPropertyType || selectedLocation || minPrice || maxPrice || selectedGenderPreference;

    if (filteredListings.length === 0 && areFiltersActive) {
        return (
            <motion.div
                className="listings-empty"
                initial="initial"
                animate="animate"
                variants={pageVariants}
            >
                <motion.h1 variants={heartVariants}>Find Your Perfect Space</motion.h1>
                <motion.div className="filter-controls" variants={sectionVariants}>
                    <select value={selectedPropertyType} onChange={(e) => setSelectedPropertyType(e.target.value)}>
                        <option value="">Property Type</option>
                        {uniquePropertyTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                    <div className="location-autocomplete-container">
                        <input
                            type="text"
                            placeholder="Search Location..."
                            value={selectedLocation}
                            onChange={handleLocationInputChange}
                            onFocus={() => { if (selectedLocation.length > 0 || uniqueLocations.length > 0) setShowSuggestions(true); }}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 100)}
                            className="location-input"
                        />
                        {showSuggestions && locationSuggestions.length > 0 && (
                            <ul className="location-suggestions-list">
                                {locationSuggestions.map((suggestion) => (
                                    <li
                                        key={suggestion}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                    >
                                        {suggestion}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <input
                        type="number"
                        placeholder="Min Price"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                    />
                    <input
                        type="number"
                        placeholder="Max Price"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)} // Corrected this to setMaxPrice
                    />
                    {/* ADDED GENDER PREFERENCE FILTER UI */}
                    <select value={selectedGenderPreference} onChange={(e) => setSelectedGenderPreference(e.target.value)}>
                        <option value="">Gender Preference</option>
                        <option value="Co-Living">Co-Living</option>
                        <option value="Girls Only">Girls Only</option>
                        <option value="Boys Only">Boys Only</option>
                    </select>
                    <button onClick={clearFilters}>Clear Filters</button>
                </motion.div>
                <motion.p variants={sectionVariants}>No listings match your current filters. Try adjusting your criteria.</motion.p>
            </motion.div>
        );
    }
    // Condition for when there are no listings at all, even without filters (initial state)
    if (listings.length === 0) return (
        <motion.div
            className="listings-list-container"
            initial="initial"
            animate="animate"
            variants={pageVariants}
        >
            <motion.h1 variants={sectionVariants}>Find Your Perfect Space</motion.h1>

            <motion.div className="roomease-approved-description" variants={sectionVariants}>
                <p>
                    Welcome to <i>RoomEase Listings</i>, your premier destination for finding the perfect <b>Flat</b> or <b>PG</b>.
                    <br/>Every listing you see here is meticulously vetted and carries our exclusive <strong>RoomEase Approved</strong> mark.
                    <br/>This signifies that each property has undergone our stringent <b>quality checks</b>, ensuring high standards for cleanliness, safety, essential amenities, and accurate descriptions. Search with confidence and discover your ideal home with complete peace of mind, knowing it meets our rigorous benchmarks.
                </p>
            </motion.div>
            <motion.div
                className="listings-empty"
                initial="initial"
                animate="animate"
                variants={sectionVariants}
            >
                No flats/PGs found yet. Check back soon!
            </motion.div>
        </motion.div>
    );

    return (
        <motion.div
            className="listings-list-container"
            initial="initial"
            animate="animate"
            variants={pageVariants}
        >
            <motion.h1 variants={heartVariants}>Find Your Perfect Space</motion.h1>

            <motion.div className="roomease-approved-description" variants={sectionVariants}>
                <p>
                    Welcome to <i>RoomEase Listings</i>, your premier destination for finding the perfect <b>Flat</b> or <b>PG</b>.
                    <br/>Every listing you see here is meticulously vetted and carries our exclusive <strong>RoomEase Approved</strong> mark.
                    <br/>This signifies that each property has undergone our stringent <b>quality checks</b>, ensuring high standards for cleanliness, safety, essential amenities, and accurate descriptions. Search with confidence and discover your ideal home with complete peace of mind, knowing it meets our rigorous benchmarks.
                </p>
            </motion.div>

            <motion.div className="filter-controls" variants={sectionVariants}>
                <select value={selectedPropertyType} onChange={(e) => setSelectedPropertyType(e.target.value)}>
                    <option value="">Property Type</option>
                    {uniquePropertyTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>

                <div className="location-autocomplete-container">
                    <input
                        type="text"
                        placeholder="Search Location..."
                        value={selectedLocation}
                        onChange={handleLocationInputChange}
                        onFocus={() => { if (selectedLocation.length > 0 || uniqueLocations.length > 0) setShowSuggestions(true); }}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 100)}
                        className="location-input"
                    />
                    {showSuggestions && locationSuggestions.length > 0 && (
                        <ul className="location-suggestions-list">
                            {locationSuggestions.map((suggestion) => (
                                <li
                                    key={suggestion}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                >
                                    {suggestion}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <input
                    type="number"
                    placeholder="Min Price"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                />
                <input
                    type="number"
                    placeholder="Max Price"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                />
                {/* ADDED GENDER PREFERENCE FILTER UI */}
                <select value={selectedGenderPreference} onChange={(e) => setSelectedGenderPreference(e.target.value)}>
                    <option value="">Gender Preference</option>
                    <option value="Co-Living">Co-Living</option>
                    <option value="Girls Only">Girls Only</option>
                    <option value="Boys Only">Boys Only</option>
                </select>
                <button onClick={clearFilters}>Clear Filters</button>
            </motion.div>

            <motion.div className="listings-grid">
                {filteredListings.map((listing, index) => (
                    // Use a staggered animation for each card
                    <motion.div
                        key={listing.id}
                        variants={listingCardVariants}
                        initial="initial"
                        animate="animate"
                        // Add a delay based on index for a staggered effect
                        transition={{ delay: index * 0.05 + 0.3, duration: 0.5, ease: "easeOut" }}
                    >
                        <Link to={`/listings/${listing.slug}`} className="listing-card-link">
                            <div className="listing-card">
                                {listing.featuredImage && listing.featuredImage.file && (
                                    <img
                                        src={listing.featuredImage.file.url}
                                        alt={listing.featuredImage.description || listing.listingName}
                                        className="listing-card-image"
                                    />
                                )}
                                <div className="listing-card-content">
                                    <h2>
                                        {listing.bhkRkSharing && (
                                            <span className="listing-config-display">{listing.bhkRkSharing} </span>
                                        )}
                                        {listing.listingName}
                                    </h2>
                                    <p className="listing-card-price">
                                        {listing.originalPrice && listing.originalPrice > listing.rentPrice ? (
                                            <>
                                                <span className="original-price">₹{listing.originalPrice}</span>{' '}
                                                <span className="discounted-price">₹{listing.rentPrice}</span>
                                            </>
                                        ) : (
                                            <span>₹{listing.rentPrice}</span>
                                        )}{' '}
                                        {listing.rentUnit || ' per month'}
                                    </p>
                                    <p className="listing-card-location">{listing.location}</p>
                                    {listing.genderPreference && ( <span className="gender-preference-value">{listing.genderPreference}</span> )}

                                    {/* NEW DISPLAY: BHK/RK/Sharing Configuration */}
                                    

                                    {/* NEW DISPLAY: Custom Urgency Message */}
                                    {listing.urgencyMessage && (
                                        <p className="urgency-message-custom">
                                            {listing.urgencyMessage}
                                        </p>
                                    )}

                                    {/* NEW DISPLAY: Gender Preference */}
                                    {/* {listing.genderPreference && (
                                        <p className="gender-preference-display">
                                            For: <span className="gender-preference-value">{listing.genderPreference}</span>
                                        </p>
                                    )} */}
                                </div>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default RoommateConnectPage;